import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  FolderTree,
  ShoppingCart,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Mail,
} from 'lucide-react';
import { logoutAdmin } from '../../services/admin/auth';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import Logo from '../layout/Logo';
import AdminThemeToggle from './AdminThemeToggle';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = async () => {
    try {
      logoutAdmin();
      navigate('/admin/login');
      toast({
        title: 'Çıkış yapıldı',
        description: 'Başarıyla çıkış yaptınız.',
      });
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: 'Hata',
        description: 'Çıkış yapılırken bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Package, label: 'Ürün Yönetimi', path: '/admin/products' },
    { icon: FolderTree, label: 'Kategori Yönetimi', path: '/admin/categories' },
    { icon: ShoppingCart, label: 'Sipariş Yönetimi', path: '/admin/orders' },
    { icon: BarChart3, label: 'Analitik', path: '/admin/analytics' },
    { icon: Mail, label: 'Bülten Aboneleri', path: '/admin/newsletter' },
    { icon: Package, label: 'Beden Rehberi', path: '/admin/size-guides' },
    { icon: FolderTree, label: 'Sayfalar', path: '/admin/pages' },
    { icon: Settings, label: 'Ayarlar', path: '/admin/settings' },
  ];

  const isActive = (path) => location.pathname === path;

  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!sidebarOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  return (
    <div className="min-h-screen bg-background">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-[min(100vw-3rem,16rem)] border-r border-border bg-card transition-transform duration-300 ease-in-out sm:w-64 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-border p-4 lg:p-6">
            <div>
              <Logo size="md" />
              <p className="mt-1 text-sm text-muted-foreground">Yönetim Paneli</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-label="Menüyü kapat"
            >
              <X size={20} />
            </Button>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto p-3 sm:space-y-2 sm:p-4">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors sm:px-4 sm:py-3 sm:text-base',
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <item.icon size={20} className="shrink-0" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="border-t border-border p-3 pb-safe sm:p-4">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex w-full items-center gap-2"
            >
              <LogOut size={18} />
              <span>Çıkış Yap</span>
            </Button>
          </div>
        </div>
      </aside>

      <div className="min-w-0 lg:pl-64">
        <header className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur-sm">
          <div className="flex items-center gap-3 px-3 py-3 sm:px-4 sm:py-4 lg:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Menüyü kapat' : 'Menüyü aç'}
            >
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </Button>
            <p className="min-w-0 flex-1 truncate text-sm font-medium text-muted-foreground lg:hidden">
              GAPPAQ Admin
            </p>
            <AdminThemeToggle className="shrink-0" />
          </div>
        </header>

        <main className="min-w-0 overflow-x-hidden p-3 sm:p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
